import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import { VehicleService } from '../../../core/services/vehicle.service';
import { Vehicle } from '../../../core/models/vehicle.model';

@Component({
  selector: 'app-vehicle-list',
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './vehicle-list.html',
  styleUrl: './vehicle-list.scss',
})
export class VehicleList {
  private readonly vehicleService = inject(VehicleService);
  private readonly fb = inject(FormBuilder);

  protected readonly vehicles = signal<Vehicle[]>([]);
  protected readonly loading = signal(true);
  protected readonly error = signal<string | null>(null);

  protected readonly modalOpen = signal(false);
  protected readonly saving = signal(false);
  protected readonly editingVehicle = signal<Vehicle | null>(null);

  protected readonly vehicleForm = this.fb.nonNullable.group({
    vehicleName: ['', Validators.required],
    make: [''],
    model: [''],
    licensePlate: [''],
    status: ['Available'],
  });

  constructor() {
    this.loadVehicles();
  }

  private loadVehicles(): void {
    this.loading.set(true);
    this.error.set(null);
    this.vehicleService.getVehicles().subscribe({
      next: (result) => {
        this.vehicles.set(result.items);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set('Failed to load vehicles. Is the FieldService API running?');
        this.loading.set(false);
        console.error(err);
      }
    });
  }

  protected openAddModal(): void {
    this.editingVehicle.set(null);
    this.vehicleForm.reset();
    this.modalOpen.set(true);
  }

  protected openEditModal(vehicle: Vehicle): void {
    this.editingVehicle.set(vehicle);
    this.vehicleForm.reset({
      vehicleName: vehicle.vehicleName ?? '',
      make: vehicle.make ?? '',
      model: vehicle.model ?? '',
      licensePlate: vehicle.licensePlate ?? '',
      status: vehicle.status ?? 'Available',
    });
    this.modalOpen.set(true);
  }

  protected closeModal(): void {
    this.modalOpen.set(false);
  }

  protected saveVehicle(): void {
    if (this.vehicleForm.invalid) {
      this.vehicleForm.markAllAsTouched();
      return;
    }

    const value = this.vehicleForm.getRawValue();
    const editing = this.editingVehicle();
    this.saving.set(true);

    if (editing != null) {
      this.vehicleService.updateVehicle(editing.vehicleId, { ...value, isActive: editing.isActive ?? true }).subscribe({
        next: (updated) => {
          this.vehicles.update((list) => list.map((v) => (v.vehicleId === updated.vehicleId ? updated : v)));
          this.saving.set(false);
          this.closeModal();
        },
        error: (err) => {
          this.saving.set(false);
          this.error.set('Failed to save vehicle.');
          console.error(err);
        }
      });
    } else {
      this.vehicleService.createVehicle(value).subscribe({
        next: (created) => {
          this.vehicles.update((list) => [...list, created]);
          this.saving.set(false);
          this.closeModal();
        },
        error: (err) => {
          this.saving.set(false);
          this.error.set('Failed to create vehicle.');
          console.error(err);
        }
      });
    }
  }

  protected deactivateVehicle(vehicle: Vehicle): void {
    this.vehicleService.deactivateVehicle(vehicle.vehicleId).subscribe({
      next: () => {
        this.vehicles.update((list) => list.map((v) => (v.vehicleId === vehicle.vehicleId ? { ...v, isActive: false, status: 'Unavailable' } : v)));
      },
      error: (err) => {
        this.error.set('Failed to deactivate vehicle.');
        console.error(err);
      }
    });
  }

  protected activateVehicle(vehicle: Vehicle): void {
    this.vehicleService.updateVehicle(vehicle.vehicleId, {
      vehicleName: vehicle.vehicleName ?? '',
      make: vehicle.make ?? undefined,
      model: vehicle.model ?? undefined,
      licensePlate: vehicle.licensePlate ?? undefined,
      status: 'Available',
      isActive: true,
    }).subscribe({
      next: (updated) => {
        this.vehicles.update((list) => list.map((v) => (v.vehicleId === updated.vehicleId ? updated : v)));
      },
      error: (err) => {
        this.error.set('Failed to activate vehicle.');
        console.error(err);
      }
    });
  }
}
